import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { CloseIcon, FacebookIcon, GoogleIcon } from '../../styles';
import {
  AlternativeButton,
  Content,
  ContentWrapper,
  ErrorMessage,
  Field,
  ForgotPasswordLink,
  Header,
  Label,
  Media,
  OrText,
  OrTextBorder,
  StyledInput,
  SubmitButton,
  SubTitle,
  SwitchModeLink,
  Title
} from './styles';

import { FacebookAuthProvider, getAuth, GoogleAuthProvider, signInWithCredential, signInWithPopup } from 'firebase/auth';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { useAppContext } from '../../../context/state';
import { getAuthErrorMessage, getUserData, signInUser } from '../../../firebase/user';
import { close, getIsFetching, setIsFetching, setShowForgot, setShowSignUp } from '../../../store/user';
import { useHomePageTag } from '../../../tag-manager/homepage';
import { userIsAdmin } from '../../../hooks/useIsAdmin';
import TermsStep, { socialSignIn } from './TermsStep';

const LoginModal = () => {
  const dispatch = useDispatch();
  const onCloseClick = () => dispatch(close());
  const onSignUpClick = () => dispatch(setShowSignUp());
  const onForgotClick = (e) => {
    e.preventDefault();
    dispatch(setShowForgot());
  };

  const isFetching = useSelector(getIsFetching);
  const { setUserData } = useAppContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const history = useHistory();

  const goToAdminIfAdmin = () =>
    userIsAdmin(getAuth().currentUser)
      .then((isAdmin) => {
        if (isAdmin) history.push({ pathname: '/admin/home' });
      })
      .catch((err) => console.error('Admin check failed:', err));

  const submitBtn = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim() === '' || password.trim() === '') {
      setError('Please fill out all fields');
    } else {
      dispatch(setIsFetching(true));
      signInUser(
        { email, password },
        () => {
          useHomePageTag({ label: 'sign in success' });

          getUserData(setUserData, (newData) => {
            if (!newData) return;
            goToAdminIfAdmin();
          });
          dispatch(setIsFetching(false));
          onCloseClick();
        },
        (error) => {
          dispatch(setIsFetching(false));
          setError(error);
        }
      );
    }
  };

  // Billing B4.4: a new Google or Facebook account goes to the terms step first.
  const [newSocialUser, setNewSocialUser] = useState(null);

  const finishSocialSignIn = async (signIn, label) => {
    try {
      dispatch(setIsFetching(true));
      const { user, isNew } = await socialSignIn(signIn);
      dispatch(setIsFetching(false));
      if (isNew) {
        setNewSocialUser(user);
        return;
      }
      getUserData(setUserData, (newData) => {
        if (!newData) return;
        goToAdminIfAdmin();
      });
      useHomePageTag({ label });
      onCloseClick();
    } catch (error) {
      console.error('Social sign-in error:', error);
      dispatch(setIsFetching(false));
      setError(getAuthErrorMessage(error, 'An error occurred during sign in. Please try again.'));
    }
  };

  const handleFacebookResponse = (response) => {
    if (!response.accessToken) {
      setError('Facebook login failed. Please try again.');
      return;
    }
    finishSocialSignIn(() => signInWithCredential(getAuth(), FacebookAuthProvider.credential(response.accessToken)), 'sign in success with facebook');
  };

  const handleGoogleSignIn = () => finishSocialSignIn(() => signInWithPopup(getAuth(), new GoogleAuthProvider()), 'sign in success with google');

  if (newSocialUser) {
    return (
      <TermsStep
        user={newSocialUser}
        onDone={() => {
          getUserData(setUserData);
          useHomePageTag({ label: 'sign up after terms step' });
          onCloseClick();
        }}
        onCancel={onCloseClick}
      />
    );
  }

  return (
    <ContentWrapper>
      <Media />
      <Content>
        <Header>
          <div>
            <Title>Sign In</Title>
            <SubTitle>Welcome back to Yedding! Select method to sign in:</SubTitle>
          </div>
          <button onClick={onCloseClick}>
            <CloseIcon />
          </button>
        </Header>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label>Email</Label>
            <StyledInput type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@gmail.com" />
          </Field>
          <Field>
            <Label>Password</Label>
            <StyledInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="**************" />
          </Field>
          <ForgotPasswordLink>
            <input type="button" onClick={onForgotClick} value="Forgot Password" />
          </ForgotPasswordLink>
          {!!error && <ErrorMessage>{error}</ErrorMessage>}
          <SubmitButton type="submit" disabled={isFetching} value={isFetching ? 'Loading...' : 'Sign In'} />
        </form>
        <OrText>
          <OrTextBorder style={{ left: 0 }} />
          <OrTextBorder style={{ right: 0 }} />
          OR
        </OrText>
        <AlternativeButton onClick={handleGoogleSignIn}>
          <GoogleIcon />
          Sign In with Google
        </AlternativeButton>
        <FacebookLogin
          appId="1091106402925547"
          autoLoad={false}
          fields="name,email,picture"
          scope="public_profile,email"
          callback={handleFacebookResponse}
          render={(renderProps) => (
            <AlternativeButton onClick={renderProps.onClick} disabled={isFetching}>
              <FacebookIcon />
              Sign In with Facebook
            </AlternativeButton>
          )}
        />
        <SwitchModeLink>
          Don't have an account?
          <button onClick={onSignUpClick}>Sign Up</button>
        </SwitchModeLink>
      </Content>
    </ContentWrapper>
  );
};

export default LoginModal;
